import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Dashboard fleet "Now Playing".
// - Poll REST /now_playing toutes les 3 s (le back agrège l'état MQTT now_playing en mémoire).
// - La barre de progression est INTERPOLÉE côté client entre deux polls (position + temps écoulé
//   réel), resynchronisée à chaque réponse -> rendu fluide sans re-solliciter le broker.
// Sémantique couleur : vert=en diffusion, ambre=téléchargement, gris=à l'arrêt, rouge=hors ligne.

const POLL_MS = 3000;
const TICK_MS = 500; // cadence d'interpolation locale de la barre
const apiBase = () => `${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}`;

const PLATFORM_META = {
    ANDROID: { label: "Android", cls: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300" },
    WEBOS: { label: "webOS", cls: "bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300" },
    TIZEN: { label: "Tizen", cls: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300" },
    ELECTRON: { label: "Electron", cls: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300" },
    WEBAPP: { label: "WebApp", cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300" },
};

const STATE_META = {
    playing: {
        label: "EN DIRECT", dot: "bg-emerald-500",
        pill: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
        bar: "bg-emerald-500", ring: "ring-emerald-200 dark:ring-emerald-500/30",
    },
    downloading: {
        label: "SYNC", dot: "bg-amber-500",
        pill: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
        bar: "bg-amber-500", ring: "ring-amber-200 dark:ring-amber-500/30",
    },
    idle: {
        label: "À L'ARRÊT", dot: "bg-slate-400",
        pill: "bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300",
        bar: "bg-slate-400", ring: "ring-slate-200 dark:ring-slate-600/40",
    },
    offline: {
        label: "HORS LIGNE", dot: "bg-red-500",
        pill: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
        bar: "bg-red-400", ring: "ring-red-200 dark:ring-red-500/30",
    },
};

function platformMeta(p) {
    return PLATFORM_META[String(p || "").toUpperCase()] || { label: p || "—", cls: "bg-slate-100 text-slate-600" };
}

function fmtClock(ms) {
    const total = Math.max(0, Math.floor((ms || 0) / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

function fmtAge(ms) {
    if (ms == null) return "—";
    const s = Math.floor(ms / 1000);
    if (s < 2) return "à l'instant";
    if (s < 60) return `il y a ${s} s`;
    const m = Math.floor(s / 60);
    return `il y a ${m} min`;
}

function StatCard({ label, value, dot }) {
    return (
        <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
            <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
            <span className="text-lg font-semibold tabular-nums text-slate-800 dark:text-slate-100">{value}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
        </div>
    );
}

function DeviceCard({ device, recvAt, now, navigate, onPreview }) {
    const meta = STATE_META[device.state] || STATE_META.offline;
    const pf = platformMeta(device.platform);
    const isPlaying = device.state === "playing";
    const hasDuration = device.durationMs > 0;

    // Interpolation : uniquement pour un device en lecture (les autres restent figés).
    const interpMs = isPlaying ? Math.max(0, now - recvAt) : 0;
    const pos = isPlaying ? (device.positionMs || 0) + interpMs : (device.positionMs || 0);
    const clampedPos = hasDuration ? Math.min(device.durationMs, pos) : pos;
    const pct = hasDuration ? Math.min(100, (clampedPos / device.durationMs) * 100) : 0;

    const dimmed = device.state === "offline" || device.state === "idle";

    return (
        <div className={`rounded-2xl bg-white p-4 shadow-sm ring-1 ${meta.ring} transition
                         dark:bg-slate-800 ${dimmed ? "opacity-75" : ""}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <button
                        type="button"
                        onClick={() => navigate(`/programme/${device.pinCode}`)}
                        title="Voir le programme de cet écran"
                        className="block max-w-full truncate text-left font-semibold text-slate-800 hover:text-indigo-600 hover:underline dark:text-slate-100 dark:hover:text-indigo-400"
                    >
                        {device.clientName || `PIN ${device.pinCode}`}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/listtizenbo")}
                        title="Voir dans les écrans approuvés"
                        className="mt-0.5 block text-xs tabular-nums text-slate-400 hover:text-indigo-600 hover:underline dark:text-slate-500 dark:hover:text-indigo-400"
                    >
                        #{device.pinCode}
                    </button>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${pf.cls}`}>
                    {pf.label}
                </span>
            </div>

            <div className="mt-3 flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.pill}`}>
                    <span className={`h-2 w-2 rounded-full ${meta.dot} ${isPlaying ? "animate-pulse" : ""}`} />
                    {meta.label}
                </span>
                {isPlaying && (
                    <span className="text-[11px] text-slate-400 dark:text-slate-500">
                        maj {fmtAge(device.ageMs == null ? null : device.ageMs + interpMs)}
                    </span>
                )}
            </div>

            <div className="mt-3 min-h-[2.5rem]">
                {isPlaying || device.state === "downloading" ? (
                    <>
                        {device.videoName ? (
                            <button
                                type="button"
                                onClick={() => onPreview(device.videoName)}
                                title="Lire cette vidéo"
                                className="flex max-w-full items-center gap-1 truncate text-left text-sm font-medium text-slate-700 hover:text-indigo-600 hover:underline dark:text-slate-200 dark:hover:text-indigo-400"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 shrink-0 opacity-70">
                                    <path d="M6.3 2.8A1 1 0 0 0 4.8 3.6v12.8a1 1 0 0 0 1.5.9l10-6.4a1 1 0 0 0 0-1.7l-10-6.4Z" />
                                </svg>
                                <span className="truncate">{device.videoName}</span>
                            </button>
                        ) : (
                            <div className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">—</div>
                        )}
                        <div className="truncate text-xs text-slate-400 dark:text-slate-500"
                             title={device.programmeName || ""}>
                            {device.programmeName || (device.programmeId ? `Programme ${device.programmeId}` : "—")}
                        </div>
                    </>
                ) : (
                    <div className="text-sm text-slate-400 dark:text-slate-500">
                        {device.state === "offline" ? "Aucun signal" : "En veille / attente"}
                    </div>
                )}
            </div>

            {/* Barre de progression */}
            <div className="mt-3">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                    <div className={`h-full rounded-full ${meta.bar} transition-[width] duration-500 ease-linear
                                     ${!hasDuration && isPlaying ? "animate-pulse w-1/3" : ""}`}
                         style={hasDuration ? { width: `${pct}%` } : undefined} />
                </div>
                <div className="mt-1 flex items-center justify-between font-mono text-[11px] tabular-nums text-slate-500 dark:text-slate-400">
                    <span>{fmtClock(clampedPos)}</span>
                    <span>{hasDuration ? fmtClock(device.durationMs) : "--:--"}</span>
                </div>
            </div>

            {/* Diag "screenshot" remonté par les apps via N8N (état de lecture réel + erreur player). */}
            {device.diag && <DeviceDiag diag={device.diag} />}
        </div>
    );
}

// Un vrai code d'erreur (≠ "aucune"/"N/A"/vide) = souci de lecture à mettre en évidence (rouge).
function hasRealError(err) {
    const e = String(err || "").trim().toLowerCase();
    return e !== "" && e !== "aucune" && e !== "n/a";
}

function DeviceDiag({ diag }) {
    const errored = hasRealError(diag.playerError);
    return (
        <div className={`mt-3 rounded-xl px-3 py-2 text-[11px] ring-1
                         ${errored
                            ? "bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30"
                            : "bg-slate-50 text-slate-500 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-400 dark:ring-slate-700"}`}>
            <div className="mb-1 flex items-center justify-between">
                <span className="font-semibold uppercase tracking-wide text-[10px]">Diagnostic</span>
                <span className="tabular-nums opacity-70">{fmtAge(diag.ageMs)}</span>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                <span>Lecture : <b className={diag.isPlaying ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}>{diag.isPlaying ? "oui" : "non"}</b></span>
                <span>MQTT : <b>{diag.mqttConnected ? "OK" : "KO"}</b></span>
            </div>
            {errored && (
                <div className="mt-1 break-words font-mono text-[10px] leading-tight">
                    ⚠ {diag.playerError}
                </div>
            )}
        </div>
    );
}

// Popup de lecture d'un média (même schéma d'URL que la médiathèque : REACT_APP_DL_IP + MOV/nom).
function VideoPreviewModal({ name, onClose }) {
    if (!name) return null;
    const src = `${process.env.REACT_APP_DL_IP}MOV/${name}`;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
             onClick={(e) => { if (e.currentTarget === e.target) onClose(); }}>
            <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-800">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 dark:border-slate-700">
                    <h3 className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100" title={name}>{name}</h3>
                    <button type="button" onClick={onClose}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700">✕</button>
                </div>
                <div className="bg-black">
                    <video key={name} src={src} controls autoPlay className="max-h-[75vh] w-full" />
                </div>
            </div>
        </div>
    );
}

export default function NowPlaying() {
    const navigate = useNavigate();
    const [preview, setPreview] = useState(null);
    const [data, setData] = useState({ devices: [], counts: {}, generatedAt: null });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showOffline, setShowOffline] = useState(false);
    const recvAtRef = useRef(Date.now());
    const [now, setNow] = useState(Date.now());

    // Poll serveur.
    useEffect(() => {
        let alive = true;
        const poll = async () => {
            try {
                const res = await axios.get(`${apiBase()}/now_playing`);
                if (!alive) return;
                recvAtRef.current = Date.now();
                setData(res.data || { devices: [], counts: {} });
                setError(null);
            } catch (e) {
                if (alive) setError("Impossible de contacter le serveur.");
            } finally {
                if (alive) setLoading(false);
            }
        };
        poll();
        const id = setInterval(poll, POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, []);

    // Tick local pour l'interpolation fluide de la barre (n'appelle pas le serveur).
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), TICK_MS);
        return () => clearInterval(id);
    }, []);

    const counts = data.counts || {};
    const devices = useMemo(() => {
        const list = data.devices || [];
        return showOffline ? list : list.filter((d) => d.state !== "offline");
    }, [data.devices, showOffline]);

    return (
        <div className="p-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Now Playing</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        État de diffusion du parc en temps réel
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <StatCard label="en diffusion" value={counts.playing || 0} dot="bg-emerald-500" />
                    <StatCard label="sync" value={counts.downloading || 0} dot="bg-amber-500" />
                    <StatCard label="à l'arrêt" value={counts.idle || 0} dot="bg-slate-400" />
                    <StatCard label="hors ligne" value={counts.offline || 0} dot="bg-red-500" />
                    <label className="ml-1 flex cursor-pointer items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                        <input type="checkbox" checked={showOffline}
                               onChange={(e) => setShowOffline(e.target.checked)}
                               className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600" />
                        hors ligne
                    </label>
                </div>
            </div>

            {error && (
                <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-300">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="py-20 text-center text-slate-400">Chargement…</div>
            ) : devices.length === 0 ? (
                <div className="rounded-2xl bg-white py-16 text-center text-slate-400 shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
                    Aucun écran en diffusion pour le moment.
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {devices.map((d) => (
                        <DeviceCard key={d.pinCode} device={d} recvAt={recvAtRef.current} now={now}
                                    navigate={navigate} onPreview={setPreview} />
                    ))}
                </div>
            )}

            <VideoPreviewModal name={preview} onClose={() => setPreview(null)} />
        </div>
    );
}
